import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { useHasPermission } from '@/hooks/use-has-permission';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useCurrentUserId } from '@/modules/service-requests/shared/employee';
import { mergeWhere } from '@/lib/table-query';
import {
  ApplicationsTable,
  useApplicationsTable,
} from '@/modules/service-requests/shared/components/applications-table';
import { useByodApplications, useWithdrawByod } from '../../hooks/use-byod';
import { ByodApplication } from '../../types/byod.types';
import { createMyByodColumns } from '../byod-columns/byod-columns';
import { ByodSpecsDialog } from '../byod-specs-dialog/byod-specs-dialog';
import { appPath, SERVICE_REQUEST_TYPES } from '@/constant/app-paths';

export const ByodMyApplications = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userId = useCurrentUserId();
  const canApply = useHasPermission(HRM_PERMISSIONS.SERVICE_REQUEST.BYOD_APPLY);
  const canWithdraw = useHasPermission(HRM_PERMISSIONS.SERVICE_REQUEST.BYOD_WITHDRAW);
  const table = useApplicationsTable();
  const [specsTarget, setSpecsTarget] = useState<ByodApplication | null>(null);
  const [withdrawTarget, setWithdrawTarget] = useState<ByodApplication | null>(null);

  const { data, isLoading, error } = useByodApplications(
    {
      pageNo: table.pagination.pageIndex + 1,
      pageSize: table.pagination.pageSize,
      filter: mergeWhere({ UserId: userId }, table.columnWhere),
      order: table.order,
    },
    Boolean(userId)
  );
  const { mutateAsync: withdraw } = useWithdrawByod();

  const columns = useMemo(
    () =>
      createMyByodColumns({
        t,
        canApply,
        canWithdraw,
        onViewSpecs: setSpecsTarget,
        onEdit: (application) =>
          navigate(appPath.serviceRequestForm(SERVICE_REQUEST_TYPES.BYOD, application.ItemId)),
        onWithdraw: setWithdrawTarget,
      }),
    [t, canApply, canWithdraw, navigate]
  );

  return (
    <>
      <ApplicationsTable
        columns={columns}
        result={data}
        isLoading={isLoading}
        error={error}
        table={table}
      />
      <ByodSpecsDialog application={specsTarget} onOpenChange={() => setSpecsTarget(null)} />
      <ConfirmationModal
        open={Boolean(withdrawTarget)}
        onOpenChange={(open) => !open && setWithdrawTarget(null)}
        title={t('WITHDRAW_BYOD')}
        confirmText={t('WITHDRAW')}
        description={t('ARE_YOU_SURE_TO_WITHDRAW_BYOD')}
        onConfirm={() => withdrawTarget && withdraw(withdrawTarget.ItemId)}
      />
    </>
  );
};
